/**
 * Fields the backend owns on every resource. They are immutable there, so the
 * app never renders an input for them and never puts them in a request body,
 * whatever the generated schema says.
 *
 * Deliberately enumerated rather than matched by suffix. Across the generated
 * schemas, `*At` splits into an audit trail the backend writes (`createdAt`,
 * `archivedAt`) and real caller input (`sendAt` in `communicatorSendRequest`,
 * `effectiveAt` in `drawPurchasesRequest`, `reportedAt` in
 * `creditreportingsRequest`). A suffix rule would silently drop the second
 * group from a request body.
 *
 * Resource-specific read-only fields (`publishedAt`, `verifiedAt`) do not
 * belong here. Those are what the spec's own `readOnly` flag is for; this list
 * is only the platform-wide invariant.
 */
export const IMMUTABLE_FIELDS: ReadonlyArray<string> = [
  'archivedAt',
  'archivedBy',
  'companyId',
  'createdAt',
  'createdBy',
  'deletedAt',
  'deletedBy',
  'id',
  'object',
  'updatedAt',
  'updatedBy',
]

const IMMUTABLE = new Set(IMMUTABLE_FIELDS)

/**
 * Matches the record's own fields only, never a nested one. `id` is this
 * record's identity; `interestRate.id` in `ratesRequest` and `teams[0].id` in
 * `employeesRequest` are references to other objects, and pointing at one is
 * the whole purpose of those fields.
 */
export const isImmutableField = (path: string): boolean =>
  IMMUTABLE.has(path) && !path.includes('.') && !path.includes('[')

/** Drops every immutable field from a request body, on create and update alike. */
export const pruneImmutableFields = <T extends { [key: string]: unknown }>(
  body: T,
): T => {
  const pruned = { ...body }
  for (const key of IMMUTABLE_FIELDS) {
    delete pruned[key]
  }
  return pruned
}
